import { required, requiredIf } from 'vuelidate/lib/validators';
import Swal from 'sweetalert2';
import api from '@/utils/api';
import utilityFunctions from '@/utils/functions';
import BottomNav from '@/components/bottom-nav';
import LoadingComponent from '@/components/loading-component';
import SubmissionClass from '@/utils/service-log-submission-class';
import * as Constants from '@/constants';
import {
  isWithinOneYear,
  isBetweenStartAndEndDate,
  isNotFutureDate,
  areMinutesGreaterThanZero,
  isValidMinutes,
  isSessionTimeEndGreaterThanSessionTime,
  isTotalReimbursableMinutesValid
} from '@/utils/validations';
import EventBus from '@/event-bus';
import StudentCard from '../student-card/student-card.vue';
import ServiceInformation from '../service-information/service-information.vue';
import MedicaidInformation from '../medicaid-information/medicaid-information.vue';
import ExtendedServiceInformation from '../extended-service-information/extended-service-information.vue';

export default {
  name: 'SlSteppers',
  components: {
    StudentCard,
    ServiceInformation,
    MedicaidInformation,
    BottomNav,
    LoadingComponent,
    ExtendedServiceInformation
  },
  props: ['student', 'medicaidArray', 'needLevelArray', 'deliveryModeArray', 'showTooltip', 'index', 'arrayLength', 'action', 'loadData', 'resetValidations', 'supervisorFlagsArray'],
  data() {
    return {
      errorMsg: '',
      // props for medicaid component
      saveOptions: '',
      // submitOptions
      save: false,
      saveAndNext: false,
      saveAndClone: false,
      showLoading: false,
      constants: Constants,
      toaster: false,
      toasterMsg: '',
      timeout: 2000,
      totalMinutesError: ''
    };
  },
  computed: {
    isMobile() {
      return this.$mq === 'mobile';
    },
    totalReimbursableMinutesErrors() {
      const errors = [];
      const totalMinutes = this.$v.student.form.totalReimbursableMinutes;
      if (!totalMinutes || !totalMinutes.$dirty) return errors;
      if (!totalMinutes.isTotalReimbursableMinutesValid) errors.push('Total Reimbursable Minutes must be less than or equal to Service Length.');
      return errors;
    }
  },
  validations() {
    let supervisorUserIdValidation =  (
      this.action === "edit" &&
      this.supervisorFlagsArray.canSupervise &&
      this.supervisorFlagsArray.supervisorRequiredFlag
    ) ? 
        {
          supervisorUserId: {
            required
          }
        }
       : {};
    
    // truncated form if needLevel is absent
    if (this.student.form.serviceInformation.needLevel && this.student.form.serviceInformation.needLevel === Constants.ABSENT) {
      return {
        student: {
          form: {
            serviceInformation: {
              needLevel: {
                required
              },
              sessionDate: {
                required,
                isWithinOneYear,
                isBetweenStartAndEndDate,
                isNotFutureDate
              },
              noShow: {
                required
              },
              location: {
                required
              },
              licenseAttestUserId: {
                required
              },
              ...supervisorUserIdValidation
            }
          }
        }
      };
    }
    // validate full form is needLevel is not absent
    return {
      student: {
        form: {
          totalReimbursableMinutes: {
            isTotalReimbursableMinutesValid
          },
          serviceInformation: {
            needLevel: {
              required
            },
            sessionDate: {
              required,
              isWithinOneYear,
              isBetweenStartAndEndDate,
              isNotFutureDate
            },
            sessionTime: {
              required
            },
            sessionTimeEnd: {
              required,
              isSessionTimeEndGreaterThanSessionTime
            },
            sessionMinutes: {
              required,
              areMinutesGreaterThanZero,
              isValidMinutes
            },
            location: {
              required
            },
            deliveryMode: {
              required
            },
            licenseAttestUserId: {
              required
            },
            ...supervisorUserIdValidation,
            roomName: {
              required: requiredIf((nestedModel) => {
                return nestedModel.deliveryMode === Constants.IN_PERSON;
              })
            }
          },
          medicaid: {
            $each: {
              minutes: {
                required,
                areMinutesGreaterThanZero,
                isValidMinutes
              },
              procedures: {
                required
              },
              outcome: {
                required
              }
            }
          }
        }
      }
    };
  },
  mounted() {
    EventBus.$on('reset-validations', this.resetForm);
  },
  methods: {
    onCancelButtonClicked() {
      if (this.action === 'edit' || this.action === 'copy') {
        this.$router.go(-1);
      } else {
        this.$emit('remove:student', this.student);
      }
    },
    resetForm() {
      this.$v.student.form.$reset();
      this.errorMsg = '';
    },
    handleSubmit(res) {
      switch (this.saveOptions) {
        case 'next':
          // add the submission to localStorage to display later
          utilityFunctions.addSubmission(res.data.data);
          // remove student from selected-students array
          this.$emit('remove:student');
          this.$v.student.$reset();
          this.saveOptions = null;
          break;
        case 'copy':
          // copies the form for the user to submit again
          utilityFunctions.addSubmission(res.data.data);
          this.student.form.serviceInformation.sessionDate = '';
          this.student.form.serviceInformation.licenseAttestUserId = null;
          this.student.form.serviceInformation.licenseAttestDate = null;
          this.student.form.serviceInformation.licenseAttestName = null;
          this.saveOptions = null;
          break;
        default:
          // default case is 'save', which saves the form and displays submissions
          utilityFunctions.addSubmission(res.data.data);
          this.$router.push('/history')
          break;
      }
    },
    showAlert() {
      return new Promise((resolve, reject) => {
        // if there is a credential name, resolve right away
        if (this.student.form.serviceInformation.credentialName) {
          resolve();
        } else {
          Swal.fire({
            type: 'warning',
            text: 'This provider has no associated credentials.',
            timer: 1000,
            showConfirmButton: false,
            onClose: () => {
              resolve();
            }
          });
        }
      });
    },
    submitForm(student) {
      this.errorMsg = '';
      this.showLoading = true;
      const formSubmission =
        new SubmissionClass(student);
      this.showAlert()
        .then(() => {
          return api.post(Constants.NSL_CREATE_URL(), formSubmission);
        })
        .then((res) => {
          this.handleSubmit(res);
        })
        .catch((err) => {
          this.errorMsg = err.response.data.message;
        })
        .finally(() => {
          this.showLoading = false;
          Swal.fire({
            type: 'success',
            text: 'Service Log has been successfully submitted!',
            showConfirmButton: false,
            timer: 1000
          });
        });
    },
    editForm() {
      this.errorMsg = '';
      this.showLoading = true;
      const formSubmission =
        new SubmissionClass(this.student);
      const sessionId = this.$route.params.sessionId
      // add session id to body to update service log
      formSubmission.sessionId = sessionId;
      return api.put(Constants.NSL_UPDATE_URL(sessionId), formSubmission)
        .then((res) => {
          this.handleSubmit(res);
        })
        .catch((err) => {
          this.errorMsg = err.response.data.message;
        })
        .finally(() => {
          Swal.fire({
            type: 'success',
            text: 'Service Log has been successfully submitted!',
            showConfirmButton: false,
            timer: 1000
          });
          this.showLoading = false;
        });
    },
    submit(student) {
      this.$v.student.form.$touch();
      if (!this.$v.student.form.$invalid) {
        this.$emit('form:submitted');
        if (this.action === 'edit') {
          this.editForm(student);
        } else {
          this.submitForm(student);
        }
      } else {
        // form isnt valid, set an error
        this.errorMsg = 'Please correct the errors above and resubmit the form';
      }
    }
  },
  watch: {
    'student.form.serviceInformation.sessionMinutes': function() {
      if (this.$v.student.form.totalMedicaidMinutes) {
        this.$v.student.form.totalMedicaidMinutes.$touch();
      }
    },
    'student.form.totalReimbursableMinutes': function () {
      if (this.$v.student.form.totalReimbursableMinutes) {
        this.$v.student.form.totalReimbursableMinutes.$touch();
      }
    },
    'student.form.totalMedicaidMinutes': function () {
      if (this.$v.student.form.totalMedicaidMinutes) {
        this.$v.student.form.totalMedicaidMinutes.$touch();
      }
    }
  }
};




// WEBPACK FOOTER //
// ./src/components/service-log/form/service-log-form/service-log-form.js